import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { Appearance, Stripe, StripePaymentElementOptions } from "@stripe/stripe-js";
import { type FormEvent, useState } from "react";

/** The storefront's Live checkout appearance, so a table is paid for in the same form. */
const APPEARANCE: Appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#00ffcc",
    colorBackground: "#0d1016",
    colorText: "#ffffff",
    colorTextSecondary: "rgba(255, 255, 255, 0.6)",
    colorTextPlaceholder: "rgba(255, 255, 255, 0.4)",
    colorDanger: "#ff3b30",
    borderRadius: "12px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSizeBase: "15px",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      boxShadow: "none",
    },
    ".Input:focus": { border: "1px solid #00ffcc", boxShadow: "none" },
    ".Tab": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      boxShadow: "none",
    },
    ".Tab--selected": { border: "1px solid #00ffcc", boxShadow: "none" },
  },
};

const PAYMENT_OPTIONS: StripePaymentElementOptions = {
  layout: { type: "tabs", defaultCollapsed: false },
  wallets: { applePay: "never", googlePay: "never" },
};

interface TablePaymentFormProps {
  stripe: Promise<Stripe | null>;
  clientSecret: string;
  /** The total, on the button. */
  amount: string;
  /** Stripe's refusal of the last try, in its words. */
  error: string | null;
  disabled: boolean;
  /** False when the card must not go to Stripe now. */
  onStart(): boolean;
  onFailed(message: string): void;
  onConfirmed(): void;
}

/** Stripe's Payment Element over a PaymentIntent, confirmed here, with no page to leave for. */
export function TablePaymentForm(props: TablePaymentFormProps) {
  return (
    <Elements
      stripe={props.stripe}
      options={{ clientSecret: props.clientSecret, appearance: APPEARANCE, loader: "auto" }}
    >
      <PaymentFields {...props} />
    </Elements>
  );
}

function PaymentFields({ amount, error, disabled, onStart, onFailed, onConfirmed }: TablePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || busy || disabled || !onStart()) return;
    setBusy(true);
    let failure: string | null = null;
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: { return_url: window.location.href },
      });
      if (error) failure = error.message ?? "The payment did not go through.";
    } catch {
      failure = "The payment could not be sent to Stripe. Try again.";
    }
    setBusy(false);
    if (failure) onFailed(failure);
    else onConfirmed();
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-4">
      <PaymentElement options={PAYMENT_OPTIONS} />
      {error && <PaymentError message={error} />}
      <button type="submit" className="btn-primary w-full" disabled={!stripe || !elements || busy || disabled}>
        {busy ? "Confirming…" : `Pay ${amount}`}
      </button>
    </form>
  );
}

export function PaymentError({ message }: { message: string }) {
  return <p className="rounded-row border border-danger/40 px-3 py-2 text-sm text-danger">{message}</p>;
}
